import { useMemo } from 'react';
import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Button, Badge } from '@sib/ui';
import { useDialog } from '@sib/ui/hooks';
import { X, Settings } from '@sib/ui/icons';
import { SibTableSettingsDialog } from './SibTableSettingsDialog';

interface SibTableSettingsProps<T> {
  table: Table<T>;
  disabledColumns?: Set<string>;
}

export const SibTableSettings = <T,>({
  table,
  disabledColumns,
}: SibTableSettingsProps<T>) => {
  const { getState, setColumnFilters } = table;
  const { t } = useTranslation();
  const settingsDialog = useDialog();

  const { rowSelection, columnFilters } = getState();

  const selectedRowsCount = useMemo(
    () => Object.entries(rowSelection).filter(([_, value]) => value).length,
    [rowSelection]
  );

  const appliedFilters = columnFilters.filter(
    f =>
      f.value !== undefined && f.value !== null && String(f.value).trim() !== ''
  );

  return (
    <>
      <div className="bg-primary sticky top-0 left-0 z-20 flex h-10 w-full items-center gap-2 rounded-tl-sm rounded-tr-sm px-4 pt-2 pb-2 sm:pb-0">
        <Button
          type="button"
          variant="outline"
          className="bg-primary hover:text-primary h-[28px] border-white text-white"
          onClick={settingsDialog.handleOpen}
        >
          <Settings className="size-5" />
        </Button>
        {selectedRowsCount > 0 && (
          <Badge variant="secondary" className="h-7 rounded-md text-nowrap">
            {t('selectedRows', {
              count: selectedRowsCount,
            })}
          </Badge>
        )}
        {appliedFilters.length > 0 && (
          <div className="flex w-full items-center justify-between">
            <span className="text-sm text-white">
              {t('appliedFilters', {
                count: appliedFilters.length,
              })}
            </span>
            <Button
              className="text-white"
              variant="ghost"
              size="custom"
              onClick={e => {
                e.stopPropagation();
                setColumnFilters([]);
              }}
            >
              <X className="size-3" />
              {t('clearAllFilters')}
            </Button>
          </div>
        )}
      </div>
      <SibTableSettingsDialog
        onClose={settingsDialog.handleClose}
        table={table}
        open={settingsDialog.isOpen}
        disabledColumns={disabledColumns}
      />
    </>
  );
};
